import React from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Link,
    Stack,
    Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {formatValue, getNestedValue, getWhiteboardDisplayTitle} from '../utils';
import RowActionsMenu from '../../../components/mapEditor/RowActionsMenu';
import AuthorChip from '../../products/components/common/AuthorChip';
import {deleteWhiteboard} from '../../../firebase/firestore/whiteboards';

/**
 * Renders a single row in the whiteboards table
 * @param {Object} props
 * @param {Object} props.whiteboard - Whiteboard data
 * @param {Array} props.columns - Column configuration
 * @param {boolean} props.isCloning - Whether this row is currently being cloned
 * @param {Function} props.onTitleClick - Title click handler
 * @param {Function} props.onTitleHover - Title hover handler
 * @param {Function} props.onClone - Clone handler (whiteboardId) => void
 * @param {Function} props.onDeleted - Called after successful deletion
 * @returns {JSX.Element}
 */
const WhiteboardRow = ({whiteboard, columns, isCloning, onTitleClick, onTitleHover, onClone, onDeleted}) => {
    const [deleteOpen, setDeleteOpen] = React.useState(false);
    const [deleting, setDeleting] = React.useState(false);
    const [deleteError, setDeleteError] = React.useState('');

    const handleTitleClick = (event) => {
        event.stopPropagation();
        onTitleClick?.(whiteboard.id);
    };

    const handleConfirmDelete = async () => {
        if (deleting) return;
        setDeleting(true);
        setDeleteError('');
        try {
            await deleteWhiteboard({whiteboardId: whiteboard.id});
            setDeleteOpen(false);
            onDeleted?.(whiteboard.id);
        } catch (err) {
            setDeleteError(err?.message || 'Failed to delete chart.');
        } finally {
            setDeleting(false);
        }
    };

    const actions = [
        {
            label: isCloning ? 'Cloning…' : 'Clone',
            icon: <ContentCopyIcon fontSize="small" />,
            onClick: () => { if (!isCloning) onClone?.(whiteboard.id); },
        },
        {
            label: 'Delete',
            icon: <DeleteOutlineIcon fontSize="small" />,
            onClick: () => setDeleteOpen(true),
            color: 'error.main',
        },
    ];

    const renderCell = (column) => {
        const value = getNestedValue(whiteboard, column.field);

        if (column.field === 'title') {
            return (
                <Stack flex={column.flex}>
                    <Link
                        component="button"
                        underline="hover"
                        onClick={handleTitleClick}
                        onMouseEnter={() => onTitleHover?.(whiteboard.id)}
                        sx={{textAlign: 'left', width: 'fit-content'}}
                    >
                        {getWhiteboardDisplayTitle(whiteboard)}
                    </Link>
                </Stack>
            );
        }

        if (column.field === 'userDisplayName') {
            return (
                <AuthorChip
                    userId={whiteboard.userId}
                    userDisplayName={whiteboard.userDisplayName}
                    flex={column.flex}
                    avatarProfile={{
                        displayName: whiteboard.userDisplayName,
                        hasAvatar: true,
                        profilePicture: null,
                    }}
                />
            );
        }

        return (
            <Typography flex={column.flex}>
                {formatValue(value)}
            </Typography>
        );
    };

    return (
        <>
            <Stack
                direction="row"
                spacing={2}
                px={2}
                py={2}
                alignItems="center"
                bgcolor="white"
                sx={{
                    '&:hover': {
                        backgroundColor: '#f9f9f9',
                    },
                }}
            >
                {columns.map((column, index) => (
                    <React.Fragment key={index}>
                        {renderCell(column)}
                    </React.Fragment>
                ))}

                <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{width: 40, minWidth: 40, flexShrink: 0}}>
                    <RowActionsMenu actions={actions} ariaLabel="Chart actions" />
                </Stack>
            </Stack>

            <Dialog open={deleteOpen} onClose={() => { if (!deleting) setDeleteOpen(false); }} fullWidth maxWidth="xs">
                <DialogTitle>Delete chart?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        <strong>{getWhiteboardDisplayTitle(whiteboard)}</strong> will be permanently deleted. This cannot be undone.
                    </Typography>
                    {deleteError && (
                        <Typography variant="body2" color="error" sx={{mt: 1}}>
                            {deleteError}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={deleting}>
                        {deleting ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default WhiteboardRow;
